import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { GIButton } from 'app-components';
import RadioButtons from '../../lib/inputs/RadioButtons';
import DatePicker from '../../lib/inputs/react-datepicker';
import { MaskedInput, ValidationErrorMessage } from '../../components/common/form';
import Input from '../../components/common/form/new/Input';
import { formatDate } from '../../utils/dates/date.utils';
import { MAX_AGE_LIMIT } from '../../constants/common.constants';
import { MM_SLASH_DD_SLASH_YYYY } from '../../utils/dates/dateFormats.utils';
import { COMPONENT_TYPES } from './common/types';
import { getFieldConfigs } from './common/getFieldConfigs';
import { useFormValidation } from './hooks/useFormValidation';
import { FormContainer, StyledField, ButtonContainer } from './ResetPasswordStyles';

type DynamicFormProps = {
    fields: (keyof ReturnType<typeof getFieldConfigs>)[];
    onSubmit: (data: Record<string, any>) => void;
};

export const AuthenticationInformationForm: React.FC<DynamicFormProps> = ({ fields, onSubmit }) => {
    const intl = useIntl();
    const fieldConfigs = getFieldConfigs(intl);

    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
        watch,
        setValue,
        reset,
    } = useFormValidation(fields, intl);

    const values = watch();

    const handleFieldChange = (field: string, value: any) => {
        setValue(field, value, { shouldValidate: true });
    };

    const handleFormSubmit = useCallback(
        (formData: Record<string, any>) => {
            const formattedData = {
                ...formData,
                ...(formData.dob && { dob: formatDate(formData.dob, MM_SLASH_DD_SLASH_YYYY) }),
            };

            onSubmit(formattedData);
            reset();
        },
        [onSubmit, reset],
    );

    return (
        <FormContainer>
            <form data-testid="dynamicSecurityQuestions" onSubmit={handleFormSubmit}>
                {fields.map(field => {
                    const config = fieldConfigs[field];
                    const labelText = intl.formatMessage({ id: config.labelId });
                    const value = values[field];

                    return (
                        <StyledField key={field} className={config?.className}>
                            {config.componentType === COMPONENT_TYPES.INPUT && (
                                <>
                                    <Input
                                        label={{
                                            labelText,
                                            labelClasses: [],
                                            wrapperClasses: [],
                                            isRequired: true,
                                        }}
                                        input={{
                                            id: field,
                                            isInputRequired: true,
                                            type: 'text',
                                            value: value ?? '',
                                            ...register(field),
                                        }}
                                        actions={{
                                            handleFieldChange: (val: string) => handleFieldChange(field, val),
                                        }}
                                    />
                                    <ValidationErrorMessage
                                        isVisible={!!errors[field]}
                                        messageText={errors?.[field]?.message}
                                    />
                                </>
                            )}

                            {config.componentType === COMPONENT_TYPES.MASKED && (
                                <>
                                    <MaskedInput
                                        label={{
                                            labelText,
                                            labelClasses: [],
                                            wrapperClasses: [],
                                            isRequired: true,
                                        }}
                                        input={{
                                            placeholder: config?.placeholder,
                                            id: field,
                                            type: 'text',
                                            isInputRequired: true,
                                            value: value ?? '',
                                            mask: config.mask,
                                            ...register(field),
                                        }}
                                        actions={{
                                            handleFieldChange: (val: string) => handleFieldChange(field, val),
                                        }}
                                    />
                                    <ValidationErrorMessage
                                        isVisible={!!errors[field]}
                                        messageText={errors?.[field]?.message}
                                    />
                                </>
                            )}

                            {config.componentType === COMPONENT_TYPES.RADIO && (
                                <>
                                    <RadioButtons
                                        label={{
                                            labelClass: [],
                                            showRequiredIcon: true,
                                            labelText: '',
                                        }}
                                        inputData={{
                                            id: field,
                                            isInputRequired: true,
                                            type: 'radio',
                                            options: config.options,
                                            value: value,
                                            ...register(field),
                                        }}
                                        legend={{
                                            dataInstructions: false,
                                            legendText: labelText,
                                        }}
                                        inputActions={{
                                            handleFieldChange: (val: string) => handleFieldChange(field, val),
                                        }}
                                    />
                                    <ValidationErrorMessage
                                        isVisible={!!errors[field]}
                                        messageText={errors?.[field]?.message}
                                    />
                                </>
                            )}

                            {config.componentType === COMPONENT_TYPES.DATE && (
                                <>
                                    <DatePicker
                                        labelText={labelText}
                                        wrapperClasses={['date-picker']}
                                        isRequired
                                        id={field}
                                        type="text"
                                        pattern={MM_SLASH_DD_SLASH_YYYY}
                                        value={value ?? ''}
                                        withMask
                                        {...register(field)}
                                        onDateChange={(val: string) => {
                                            handleFieldChange(field, val || '');
                                            setValue(field, val || '', { shouldValidate: true });
                                        }}
                                        noOfYearsToShow={MAX_AGE_LIMIT}
                                    />
                                    <ValidationErrorMessage
                                        isVisible={!!errors[field]}
                                        messageText={errors?.[field]?.message}
                                    />
                                </>
                            )}
                        </StyledField>
                    );
                })}

                <ButtonContainer>
                    <GIButton
                        id="submit_btn"
                        labelId="resetPassword.button.submit"
                        isDisabled={!isValid}
                        onClickHandler={handleSubmit(handleFormSubmit)}
                    />
                </ButtonContainer>
            </form>
        </FormContainer>
    );
};
